import i18n, { Ti18nKeys } from "../../../constants/i18n";
import ET3Div from "../../../core/et3-div";
import { withLiteObserverAndTheme } from "../../../core/et3-enhanced-render";
import Et3Text from "../../../core/et3-text";
import {
  SURFACE_BORDER,
  SURFACE_RADIUS_CSS,
  SURFACE_SHADOW
} from "../../../core/et3-theme/surface-constants";
import getApproveRejectStore from "../../../stores/approval-reject-store";
import {
  STRIP_ACCENT_WIDTH,
  STRIP_DIVIDER_WIDTH,
  STRIP_ITEM_GAP,
  STRIP_LABEL_FONT_SIZE,
  STRIP_LANE_FONT_SIZE,
  STRIP_PADDING_X,
  STRIP_PADDING_Y,
  STRIP_ROW_GAP,
  STRIP_TOTAL_FONT_SIZE
} from "./approval-data-constants";

const StripLane = withLiteObserverAndTheme<{
  labelKey: Ti18nKeys;
  value: string;
}>((props) => {
  const { labelKey, value, theme } = props;
  const { colors, unitX } = theme;

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={false}
      withShadow={false}
      width={0}
      height={0}
      fDirection="row"
      alignItems="center"
      justifyContent="flex-start"
      color={colors.all.transparent}
      style={{
        width: "auto",
        minWidth: 0,
        height: "auto",
        minHeight: 0,
        flexShrink: 0,
        gap: 6 * unitX
      }}
      onClick={() => {}}
    >
      <Et3Text
        fontFamily={"LIGHT"}
        fontSize={STRIP_LANE_FONT_SIZE}
        text={i18n.get(labelKey)}
        isSpan={true}
        color={colors.all.davysGrey}
        style={{ margin: 0, whiteSpace: "nowrap" }}
      />
      <Et3Text
        fontFamily={"BOLD"}
        fontSize={STRIP_LANE_FONT_SIZE}
        text={value}
        isSpan={true}
        color={colors.all.maastrichtBlue}
        style={{ margin: 0 }}
      />
    </ET3Div>
  );
});

const StripDivider = withLiteObserverAndTheme((props) => {
  const { theme } = props;
  const { colors } = theme;

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={STRIP_DIVIDER_WIDTH}
      height={0}
      fDirection="column"
      alignItems="stretch"
      justifyContent="flex-start"
      color={colors.all.shadeOfGray}
      style={{ alignSelf: "stretch", flexShrink: 0 }}
      onClick={() => {}}
    />
  );
});

const ApprovalQueueStrip = withLiteObserverAndTheme((props) => {
  const { theme } = props;
  const { colors, unitX, unitY } = theme;
  const store = getApproveRejectStore();
  const pendingItems = store.approveRejectCounter.get();
  const hasPendingWork = pendingItems > 0;

  return (
    <ET3Div
      isScrollable={false}
      isExtendable={true}
      withShadow={false}
      width={0}
      height={0}
      fDirection="row"
      alignItems="stretch"
      justifyContent="flex-start"
      color={colors.all.white}
      style={{
        boxSizing: "border-box",
        width: "100%",
        minWidth: 0,
        border: SURFACE_BORDER,
        borderRadius: SURFACE_RADIUS_CSS,
        boxShadow: SURFACE_SHADOW,
        overflow: "hidden"
      }}
      onClick={() => {}}
    >
      <ET3Div
        isScrollable={false}
        isExtendable={true}
        withShadow={false}
        width={STRIP_ACCENT_WIDTH}
        height={0}
        fDirection="column"
        alignItems="stretch"
        justifyContent="flex-start"
        color={hasPendingWork ? colors.all.darkOrange : colors.all.green}
        style={{ flexShrink: 0 }}
        onClick={() => {}}
      />
      <ET3Div
        isScrollable={false}
        isExtendable={true}
        withShadow={false}
        width={0}
        height={0}
        fDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        color={colors.all.transparent}
        style={{
          boxSizing: "border-box",
          flex: 1,
          minWidth: 0,
          flexWrap: "wrap",
          padding: `${STRIP_PADDING_Y * unitY}px ${STRIP_PADDING_X * unitX}px`,
          gap: `${STRIP_ROW_GAP * unitY}px ${STRIP_ITEM_GAP * unitX}px`
        }}
        onClick={() => {}}
      >
        <ET3Div
          isScrollable={false}
          isExtendable={false}
          withShadow={false}
          width={0}
          height={0}
          fDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          color={colors.all.transparent}
          style={{
            width: "auto",
            minWidth: 0,
            height: "auto",
            minHeight: 0,
            flexShrink: 0,
            gap: 8 * unitX
          }}
          onClick={() => {}}
        >
          <Et3Text
            fontFamily={"BOLD"}
            fontSize={STRIP_TOTAL_FONT_SIZE}
            text={`${pendingItems}`}
            isSpan={true}
            color={colors.all.maastrichtBlue}
            style={{ margin: 0 }}
          />
          <Et3Text
            fontFamily={"MEDIUM"}
            fontSize={STRIP_LABEL_FONT_SIZE}
            text={i18n.get("APPROVAL_ITEMS_PENDING")}
            isSpan={true}
            color={colors.all.davysGrey}
            style={{
              margin: 0,
              textTransform: "uppercase",
              letterSpacing: 0.6,
              whiteSpace: "nowrap"
            }}
          />
        </ET3Div>
        <StripDivider />
        <StripLane
          labelKey={"PICTURES"}
          value={`${store.pendingPicturesCounter.get()}`}
        />
        <StripLane
          labelKey={"DESCRIPTION_ABOUT_ME"}
          value={`${store.pendingSelfDescriptionCounter.get()}`}
        />
        <StripLane
          labelKey={"DESCRIPTION_ABOUT_MY_PARTNER"}
          value={`${store.pendingPeerDescriptionCounter.get()}`}
        />
        <StripDivider />
        <StripLane
          labelKey={"APPROVAL_USERS_WITH_WORK"}
          value={`${store.usersWithPendingWorkCounter.get()} ${i18n.getWithExtraData(
            "APPROVAL_OF_POOL",
            `${store.approvalData.get().length}`
          )}`}
        />
        <StripDivider />
        <StripLane
          labelKey={"APPROVAL_DECIDED_THIS_SESSION"}
          value={`${store.decidedThisSession.get()}`}
        />
      </ET3Div>
    </ET3Div>
  );
});
export default ApprovalQueueStrip;
